import { useContext, useState, useCallback, useMemo } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { InteractionStepType, StepParamsForType } from '@scale/shared/types/chat/params';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import { useChatStoreRef } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { useContributorFeedbackImperativeApi } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import {
  ActionTakenEnum,
  ContributorFeedback,
  FeedbackType,
  FetchContributorFeedbackForStepResponse,
  InteractionStepResponse,
  belongsToStep,
  sanitizeStepResponses,
} from '@scale/shared/types/chat';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import logger from '@scale/frontend-lib/logging';
import _ from 'lodash';
import { logEvents } from 'react-scale/lib/events';
import { Event } from '@scale/shared/types/events/index';
import { LintSeverity } from '@scale/shared/task_lint/task_linter';
import { GenAILinterFeedbackEvent } from '@scale/shared/types/events/GenAILinterFeedbackEvent';
import { UnifiedFeedbackContributorActionEvent } from '@scale/shared/types/events/UnifiedFeedbackEvents/UnifiedFeedbackContributorActionEvent';
import { UnifiedFeedbackContributorRatingEvent } from '@scale/shared/types/events/UnifiedFeedbackEvents/UnifiedFeedbackContributorRatingEvent';
import { computeShouldSkipLinting } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { useEventBus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';
import useMarkdownValidator from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/hooks/useMarkdownValidator';
import { validateStepMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/validation/validateStepMarkdown';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useMarkdownSyntaxOptions } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMarkdownSyntaxOptions';

const getCanSubmit = (feedback: ContributorFeedback) => {
  const automatedFeedback = feedback.filter(
    feedbackItem => feedbackItem.type !== FeedbackType.HumanReviewerFeedback,
  );
  const hasNoBlockingFeedbackItems =
    automatedFeedback.filter(feedbackItem => feedbackItem.isBlocking).length === 0;
  const passFeedback =
    hasNoBlockingFeedbackItems &&
    automatedFeedback.filter(
      feedbackItem =>
        !feedbackItem.isBlocking && feedbackItem.action?.actionTaken !== ActionTakenEnum.Dismissed,
    ).length === 0;

  // Note: Unlike with `usePerStepResponseLinter`, unified feedback doesn't have a toggle that requires
  // rating of validators/feedback, so we don't need the `hasCompletedFeedback` check that we have
  // in the same `getCanSubmit` function in `usePerStepResponseLinter`.

  return {
    canSubmit: passFeedback,
    passFeedback,
  };
};

/**
 * This function can be extended in the future to include other types of automated feedback that should be preserved
 * upon submission of a step response and should not be accounted for when validating a step. At the moment, it only
 * preserves AutoQC feedback because AutoQC feedback is not generated on step submission, rather it is generated
 * when the task attempt is first loaded, and we should not be wiping it out when revalidating a step response.
 */
const getAutomatedFeedbackToIgnore = (
  feedback: ContributorFeedback,
): [ContributorFeedback, ContributorFeedback] => {
  return _.partition(feedback, ({ type }) => type === FeedbackType.AutoQC);
};

export interface PerStepContributorFeedbackHookParams {
  stepParams: StepParamsForType<any> & StepParamsAuxiliary;
  disableFeedback?: boolean;
}

export const usePerStepContributorFeedback = ({
  stepParams,
  disableFeedback,
}: PerStepContributorFeedbackHookParams) => {
  const { taskId, projectId, assignmentId, workerId, milkdownEditorConfig, editorConfig } =
    useContext(ChatTaskContext);

  const eventBus = useEventBus();

  const chatStoreRef = useChatStoreRef();

  const stepWithIndexKey = constructStepChildId(stepParams);

  const contributorFeedbackStoreInstance = useContributorFeedbackImperativeApi();

  const markdownSyntaxOptions = useMarkdownSyntaxOptions();

  const { validate: validateMarkdown } = useMarkdownValidator({
    enableMath: milkdownEditorConfig?.enableMathPlugin,
    defaultTokens: markdownSyntaxOptions,
  });

  const isMarkdownValidationLoggingEnabled = useUnleashFeatureFlag(
    'chat-enable-step-submission-markdown-validation-logging',
    {
      projectId,
    },
    false,
  );

  /**
   * The ModelResponseEditor is the most common step we use the markdown editor for. There's few
   * other steps that may also use the markdown editor, like ProcessSupervision, but we'll omit
   * validation for those steps for now given that they're not as common.
   */
  const isStepUsingTipTapMarkdownEditor = useMemo(
    () =>
      !editorConfig?.enablePlaintextEditor &&
      stepParams.type === InteractionStepType.ModelResponseEditor,
    [editorConfig, stepParams],
  );

  const [isLoadingContributorFeedback, setIsLoadingContributorFeedback] = useState(false);

  const logFeedbackItemActionAndRating = useCallback(async () => {
    const unifiedFeedbackRatingEvents: UnifiedFeedbackContributorRatingEvent[] = [];
    const unifiedFeedbackActionEvents: UnifiedFeedbackContributorActionEvent[] = [];
    const linterFeedbackEvents: GenAILinterFeedbackEvent[] = [];
    const currentContributorFeedbackStoreState = contributorFeedbackStoreInstance.getState();
    const feedback = currentContributorFeedbackStoreState.feedback.filter(({ childId }) =>
      belongsToStep(childId, stepWithIndexKey),
    );

    for (const feedbackItem of feedback) {
      if (feedbackItem.helpfulness) {
        unifiedFeedbackRatingEvents.push({
          projectId,
          workerId,
          assignmentId,
          rating: feedbackItem.helpfulness,
          feedbackType: feedbackItem.type,
          feedbackItem,
        });
        /**
         * Necessary for backwards compatibility with the old linter feedback logging.
         * Will add Snowflake events for Unified Feedback specifically in a future PR.
         */
        if (feedbackItem.type === FeedbackType.Linters) {
          linterFeedbackEvents.push({
            linterName: feedbackItem.title,
            projectId,
            workerId,
            assignmentId,
            frontendSeverity: feedbackItem.isBlocking
              ? LintSeverity.ERROR
              : feedbackItem.isJustificationRequired
              ? LintSeverity.DISMISSABLE_ERROR
              : LintSeverity.WARNING,
            linterFrontendFeedback: feedbackItem.helpfulness,
            genAILinterEventRowId: feedbackItem.metadata?.genAILinterEventRowId,
          });
        }
      }

      if (feedbackItem.action) {
        unifiedFeedbackActionEvents.push({
          projectId,
          workerId,
          assignmentId,
          action: feedbackItem.action?.actionTaken,
          feedbackType: feedbackItem.type,
          feedbackItem,
        });
      }
    }

    try {
      await Promise.all([
        logEvents(Event.UnifiedFeedbackContributorRatingEvent, unifiedFeedbackRatingEvents),
        logEvents(Event.UnifiedFeedbackContributorActionEvent, unifiedFeedbackActionEvents),
        logEvents(Event.GenAILinterFeedbackEvent, linterFeedbackEvents),
      ]);
    } catch (err) {
      logger.error('usePerStepContributorFeedback - failed to log events', { err });
    }
  }, [assignmentId, projectId, workerId, contributorFeedbackStoreInstance, stepWithIndexKey]);

  /** TODO: Function is sort of messy - should circle back in a future PR to clean up */
  const fetchContributorFeedbackOnStepSubmit = useCallback(
    // eslint-disable-next-line complexity
    async (newResponse: InteractionStepResponse, index: number) => {
      const currentChatStoreState = chatStoreRef.current.getState();
      const setParamsAtLastCheck = currentChatStoreState.setPerStepLinterParamsAtLastCheck;
      const setHasLinterErrorForIndex = currentChatStoreState.setHasLinterErrorForIndex;
      const setIsValidating = currentChatStoreState.setIsPerStepLinterValidating;
      const clearStepLinterErrors = currentChatStoreState.clearStepLinterErrors;

      try {
        /**
         * Filtering logic copied over from `lintStepResponse` in `usePerStepResponseLinter.ts`.
         */
        const shouldSkip = computeShouldSkipLinting({
          projectId,
          assignmentId,
          stepParams,
          stepResponse: newResponse,
          eventBus,
          disableLinter: disableFeedback,
        });

        if (shouldSkip) {
          return true;
        }

        const taskParams = currentChatStoreState.config;
        const reviewLevel = currentChatStoreState.reviewLevel;
        const allStepParams = currentChatStoreState.stepParams;
        const paramsAtLastCheck = currentChatStoreState.perStepLinterParamsAtLastCheck;

        const currentMultiChatIndex = currentChatStoreState.currentMultiChatIndex;

        // We set it to match the UI checkboxes, which are 1-indexed
        const currentMultiChatPage = !_.isNil(currentMultiChatIndex)
          ? (currentMultiChatIndex + 1).toString()
          : undefined;

        setIsLoadingContributorFeedback(true);
        setIsValidating(true);
        clearStepLinterErrors();

        const currentContributorFeedbackStoreState = contributorFeedbackStoreInstance.getState();
        const setFeedback = currentContributorFeedbackStoreState.setFeedbackState;
        const feedback = currentContributorFeedbackStoreState.feedback.filter(({ childId }) =>
          belongsToStep(childId, stepWithIndexKey),
        );
        const [workerFeedback, automatedFeedback] = _.partition(
          feedback,
          ({ type }) =>
            // Filter worker feedback
            type === FeedbackType.HumanReviewerFeedback,
        );

        /**
         * Sanitizes step responses the same way linters do so
         */
        const { sanitizedStepResponse, sanitizedResponses } = sanitizeStepResponses({
          currStepResponse: newResponse,
          allStepResponses: currentChatStoreState.responses,
          currStepParams: stepParams,
          allStepParams,
        });

        const requestMetadata = {
          responses: sanitizedResponses,
          taskParams,
          reviewLevel,
          stepId: stepParams.id,
          stepType: stepParams.type,
          currentResponseIndex: index,
          currentMultiChatPage,
        };

        const requestParams = {
          projectId: projectId ?? '',
          assignmentId: assignmentId ?? '',
          stepResponse: sanitizedStepResponse,
          metadata: requestMetadata,
        };

        const [feedbackToIgnore, feedbackToValidate] = getAutomatedFeedbackToIgnore(feedback);
        let { canSubmit, passFeedback } = getCanSubmit(feedbackToValidate);

        const shouldRerun = !_.isEqual(paramsAtLastCheck, requestParams);
        if (!shouldRerun) {
          if (!passFeedback) setHasLinterErrorForIndex(index, true);
          if (canSubmit) {
            await logFeedbackItemActionAndRating();
            setFeedback([...workerFeedback, ...feedbackToIgnore], stepWithIndexKey);
            setParamsAtLastCheck(undefined);
          }

          return canSubmit;
        }

        const newDismissedFeedback = automatedFeedback.filter(
          feedbackItem =>
            feedbackItem.type === FeedbackType.AutoFeedback &&
            feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed,
        );

        const prevDismissedFeedback =
          currentContributorFeedbackStoreState.getPrevDismissedPerStepFeedback(stepWithIndexKey) ??
          [];

        await logFeedbackItemActionAndRating();
        setParamsAtLastCheck(requestParams);

        let markdownFeedback: ContributorFeedback = [];
        try {
          markdownFeedback = await validateStepMarkdown({
            taskId,
            assignmentId,
            projectId,
            workerId,
            stepResponse: sanitizedStepResponse,
            responseIndex: index,
            stepId: stepParams.id,
            validationOptions: {
              // Only log validation issues if the step is using a TipTap markdown editor. Logging is disabled for the plaintext editor
              isMarkdownValidationLoggingEnabled:
                !!isMarkdownValidationLoggingEnabled && !!isStepUsingTipTapMarkdownEditor,
              // Omit feedback if the step is using a TipTap markdown editor
              shouldOmitFeedback: !!isStepUsingTipTapMarkdownEditor,
            },
            markdownValidationFn: validateMarkdown,
          });
        } catch (e) {
          logger.error(`Error validating markdown for step`, {
            assignmentId,
            projectId,
            workerId,
            stepId: stepParams.id,
            stepType: stepParams.type,
            responseIndex: index,
            errorMessage: e.message,
            errorStack: e.stack,
          });
        }

        /**
         * Catching API errors here for backwards compatibility with previous timeout behavior
         */
        let contributorFeedback: FetchContributorFeedbackForStepResponse;
        try {
          contributorFeedback = await API.genai.fetchContributorFeedbackForStep({
            ...requestParams,
            prevDismissedPerStepFeedback: [...prevDismissedFeedback, ...newDismissedFeedback],
          });
        } catch (e) {
          if (e instanceof Error) {
            logger.error(
              `[usePerStepContributorFeedback] Error fetching contributor feedback for step`,
              {
                assignmentId,
                projectId,
                workerId,
                stepId: stepParams.id,
                stepType: stepParams.type,
                responseIndex: index,
                errorMessage: e.message,
                errorStack: e.stack,
              },
            );
          } else {
            logger.error(
              `[usePerStepContributorFeedback] Error fetching contributor feedback for step: Non-Error thrown`,
              {
                assignmentId,
                projectId,
                workerId,
                stepId: stepParams.id,
                stepType: stepParams.type,
                responseIndex: index,
                thrownValue: JSON.stringify(e),
              },
            );
          }
          contributorFeedback = { feedback: [] };
        }
        setIsLoadingContributorFeedback(false);

        const contributorFeedbackWithChildId = [
          ...contributorFeedback.feedback,
          ...markdownFeedback,
        ].map(feedbackItem => ({
          ...feedbackItem,
          childId: stepWithIndexKey,
        }));

        // Add dismissed AutoFeedback items to the store. Currently used ONLY for the AutoFeedback disagree mechanism.
        // We don't add other types of dismissed feedback to this list yet because other feedback type handlers don't
        // need access to dismissed feedback for anything yet.
        currentContributorFeedbackStoreState.addDismissedPerStepFeedback(
          newDismissedFeedback,
          stepWithIndexKey,
        );

        ({ canSubmit, passFeedback } = getCanSubmit(contributorFeedbackWithChildId));
        if (!passFeedback) setHasLinterErrorForIndex(index, true);
        if (canSubmit) {
          setFeedback([...workerFeedback, ...feedbackToIgnore], stepWithIndexKey);
          setParamsAtLastCheck(undefined);
        } else {
          setFeedback(
            [...contributorFeedbackWithChildId, ...feedbackToIgnore, ...workerFeedback],
            stepWithIndexKey,
          );
        }

        return canSubmit;
      } catch (e) {
        logger.error(`Fetching Contributor Feedback for [${assignmentId}]: Error: [${e}]`);
        defaultApiFetchErrorHandler(e, {
          handleHttp: true,
          message: 'Something went wrong while fetching contributor feedback for the response.',
        });
      } finally {
        setIsValidating(false);
        setIsLoadingContributorFeedback(false);
      }
    },
    [
      chatStoreRef,
      taskId,
      projectId,
      assignmentId,
      stepParams,
      eventBus,
      disableFeedback,
      contributorFeedbackStoreInstance,
      stepWithIndexKey,
      logFeedbackItemActionAndRating,
      isMarkdownValidationLoggingEnabled,
      isStepUsingTipTapMarkdownEditor,
      workerId,
      validateMarkdown,
    ],
  );

  return {
    isLoadingContributorFeedback,
    fetchContributorFeedbackOnStepSubmit,
    stepWithIndexKey,
  };
};
